import { OpenItem, OpenItemPriority, OpenItemStatus } from "@/api/open-items";
import { pluralize } from "@/lib/utils";
import { format } from "date-fns";

export const ACTIVE_OPEN_ITEM_STATUSES: OpenItemStatus[] = [
  "open",
  "in_progress",
  "blocked",
];

export const OPEN_ITEM_STATUS_LABELS: Record<OpenItemStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  blocked: "Blocked",
  resolved: "Resolved",
};

export const OPEN_ITEM_STATUS_COLORS: Record<OpenItemStatus, string> = {
  open: "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100",
  in_progress: "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
  blocked: "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100",
  resolved:
    "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
};

export const OPEN_ITEM_STATUS_BARS: Record<OpenItemStatus, string> = {
  open: "bg-sky-500",
  in_progress: "bg-amber-500",
  blocked: "bg-rose-500",
  resolved: "bg-emerald-500",
};

export const OPEN_ITEM_PRIORITY_LABELS: Record<OpenItemPriority, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

export const OPEN_ITEM_PRIORITY_COLORS: Record<OpenItemPriority, string> = {
  critical:
    "border-fuchsia-200 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100",
  high: "border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100",
  medium: "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
  low: "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100",
};

const OPEN_ITEM_PRIORITY_BARS: Record<OpenItemPriority, string> = {
  critical: "bg-fuchsia-600",
  high: "bg-rose-500",
  medium: "bg-amber-500",
  low: "bg-slate-400",
};

export const OPEN_ITEM_AI_FIELD_LABELS: Record<string, string> = {
  title: "Title",
  group_name: "Group",
  closing_evidence: "Closes when",
  description: "Why it matters",
  next_step: "Next step",
  priority: "Priority",
  source_is_stale: "Source status",
};

/** The heading for a board group, whichever field the board groups by. */
export function openItemGroupLabel(groupBy: string, key: string): string {
  if (groupBy === "status")
    return OPEN_ITEM_STATUS_LABELS[key as OpenItemStatus];
  if (groupBy === "priority")
    return OPEN_ITEM_PRIORITY_LABELS[key as OpenItemPriority];
  return key;
}

/** The accent bar beside a group heading: the status or priority colour, else the brand. */
export function openItemGroupAccent(groupBy: string, key: string): string {
  if (groupBy === "status") return OPEN_ITEM_STATUS_BARS[key as OpenItemStatus];
  if (groupBy === "priority") {
    return OPEN_ITEM_PRIORITY_BARS[key as OpenItemPriority];
  }
  return "bg-primary";
}

export function openItemOwnerInitials(name?: string | null): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

/** Human comments versus recorded system changes on an item. */
export function openItemCommentCounts(item: OpenItem) {
  const comments = item.human_comment_count;
  return { comments, changes: Math.max(item.comment_count - comments, 0) };
}

/** "2 comments · 3 changes", or null when there is nothing yet. */
export function openItemActivityLabel(item: OpenItem): string | null {
  const { comments, changes } = openItemCommentCounts(item);
  if (!comments && !changes) return null;
  return `${pluralize(comments, "comment")}${
    changes ? ` · ${pluralize(changes, "change")}` : ""
  }`;
}

export function isOpenItemOverdue(item: OpenItem): boolean {
  return (
    !!item.due_date &&
    item.status !== "resolved" &&
    item.due_date < format(new Date(), "yyyy-MM-dd")
  );
}
